'use client';

import { useIsMobile } from '@/hooks/useWindowDimensions';
import { Drawer } from '@heidi/design-system/drawer';
import { DropdownMenu } from '@heidi/design-system/dropdown-menu';
import * as React from 'react';

import { NavContext, type NavContextValue, VariantContext } from './context';

/**
 * AdaptiveMenu renders a desktop dropdown (anchored popover with side-flyout
 * submenus) or, on mobile, a bottom-sheet Drawer where submenus become nested
 * push-drawers. The API mirrors `DropdownMenu` so call sites can swap one for
 * the other. Side-flyout submenus get clipped on narrow viewports; the drawer
 * lays panels out full-width instead.
 */

type AdaptiveMenuProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
};

export const AdaptiveMenu = ({ open, onOpenChange, children }: AdaptiveMenuProps) => {
  const isMobile = useIsMobile();
  const [stack, setStack] = React.useState<
    Array<{ id: string; onOpenChange?: (open: boolean) => void }>
  >([]);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) {
        // closing the drawer collapses every open panel, so each reports closed
        stack.forEach((panel) => panel.onOpenChange?.(false));
        setStack([]);
      }
      onOpenChange?.(next);
    },
    [onOpenChange, stack]
  );

  const nav = React.useMemo<NavContextValue>(
    () => ({
      activePanelId: stack[stack.length - 1]?.id ?? null,
      push: (id, cb) => {
        setStack((prev) => [...prev, { id, onOpenChange: cb }]);
        cb?.(true);
      },
      pop: () => {
        stack[stack.length - 1]?.onOpenChange?.(false);
        setStack((prev) => prev.slice(0, -1));
      },
      close: () => handleOpenChange(false),
    }),
    [stack, handleOpenChange]
  );

  if (!isMobile) {
    return (
      <VariantContext.Provider value="menu">
        <DropdownMenu open={open} onOpenChange={onOpenChange}>
          {children}
        </DropdownMenu>
      </VariantContext.Provider>
    );
  }

  return (
    <VariantContext.Provider value="drawer">
      <NavContext.Provider value={nav}>
        <Drawer open={open} onOpenChange={handleOpenChange}>
          {children}
        </Drawer>
      </NavContext.Provider>
    </VariantContext.Provider>
  );
};
